import { useNavigate } from "@tanstack/react-router"
import type { Row, RowSelectionState } from "@tanstack/react-table"
import React, { useCallback } from "react"

import type { ProductEntity } from "./productColumns"
import { columns } from "./productColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { InstructionProduct, Product } from "@/graphql/codegen/graphql"

interface AssociatedProductsTableProps {
  products: InstructionProduct[] | Product[]
  productsFetching?: boolean
  isSelectable?: boolean
  associatedProductIds?: number[]
  selected?: Record<string, boolean>
  onProductSelection?: (selected: Record<string, boolean>) => void
}

export const AssociatedProductsTable: React.FC<AssociatedProductsTableProps> = ({
  products,
  productsFetching = false,
  isSelectable = false,
  selected = {},
  onProductSelection,
  associatedProductIds = [],
}) => {
  const navigate = useNavigate()

  // Do not show the first column with checkboxes if not selectable
  const tableColumns = React.useMemo(() => {
    if (!isSelectable) {
      return columns.slice(1)
    }
    return columns
  }, [isSelectable])

  const handleRowSelectionChange = useCallback(
    (updaterOrValue: ((old: RowSelectionState) => RowSelectionState) | RowSelectionState) => {
      const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(selected) : updaterOrValue

      onProductSelection?.(newSelection)
    },
    [selected, onProductSelection]
  )

  const handleRowClick = useCallback(
    (row: Row<ProductEntity>) => {
      if (isSelectable) {
        return
      }
      navigate({ to: `/app/product/${row.original.productId}/edit/information` })
    },
    [isSelectable, navigate]
  )
  return (
    <AerosBaseTable
      slotProps={{
        bodyCellExpanded: { className: "bg-primary-100" },
        bodyRow: {
          className: "hover:bg-primary-100 cursor-pointer",
          onClick: (row) => {
            if ("original" in row) {
              handleRowClick(row)
            }
          },
        },
      }}
      features={{
        tanstackOptions: {
          data: products,
          columns: tableColumns,
          onRowSelectionChange: handleRowSelectionChange,
          getRowId: (row) => row?.productId?.toString() as string,
          state: {
            isLoading: productsFetching,
            rowSelection: selected,
          },
          // prevent selection of rows that are already associated
          enableRowSelection: (row) => !associatedProductIds.includes(row.original.productId),
        },
      }}
      noDataMessage='No products added'
    />
  )
}
